import React, { useState } from 'react';
import { Box, Typography, Avatar, Chip, Link as MuiLink, useTheme, useMediaQuery, Button } from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { Review } from '../../types/Review';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import Dialog from '../dialogs/Dialog';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { likeReview, unlikeReview, toggleReviewHelpfulness, removeReviewHelpfulness } from '../../services/api';
import UpdateIcon from '@mui/icons-material/Update';
import { getPlatformName, getPlatformDisplayText } from '../../utils/PlatformList';
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined';
import { formatReviewText } from '../../utils/formatText';
import { effectivePlayTimeHours } from '../../utils/playtime';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import DetailScores from './DetailScores';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Tooltip from '@mui/material/Tooltip';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import { scoreTags } from '../../utils/scoreTags';


interface ReviewItemProps {
  review: Review;
  isListView?: boolean;
}

const ReviewItem: React.FC<ReviewItemProps> = ({ review, isListView = false }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isLiked, setIsLiked] = useState(review.is_liked);
  const [likeCount, setLikeCount] = useState(review.like_count);
  const [userLikeCount, setUserLikeCount] = useState(review.user_like_count || 0);
  const [isHelpful, setIsHelpful] = useState<boolean | null>(review.is_helpful);
  const [helpfulCount, setHelpfulCount] = useState(review.helpful_count);
  const [unhelpfulCount, setUnhelpfulCount] = useState(review.unhelpful_count);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  const maxLength = isListView ? (isMobile ? 500 : 1000) : (isMobile ? 120 : 180);
  const maxLines = isListView ? (isMobile ? 15 : 30) : (isMobile ? 4 : 5);


  const toggleExpand = () => {
    setExpanded(true);
  };

  const truncateText = (text: string) => {
    const lines = text.split('\n');
    if (lines.length > maxLines || text.length > maxLength) {
      const truncatedLines = lines.slice(0, maxLines);
      let truncatedText = truncatedLines.join('\n');
      if (truncatedText.length > maxLength) {
        truncatedText = truncatedText.slice(0, maxLength);
      }
      return `${truncatedText}...`;
    }
    return text;
  };

  const truncatedText = expanded ? review.review_text : truncateText(review.review_text);

  const formatText = (text: string) => {
    return text.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const formatPlayDate = (startDate: string | null) => {
    if (!startDate) return '不明';
    const start = new Date(startDate);
    const now = new Date();
    const yearDiff = now.getFullYear() - start.getFullYear();
    const monthDiff = now.getMonth() - start.getMonth();
    const totalMonthDiff = yearDiff * 12 + monthDiff;

    if (totalMonthDiff < 1) {
      return '今月';
    } else if (totalMonthDiff < 12) {
      return `${totalMonthDiff}ヶ月前`;
    } else {
      return `${Math.floor(totalMonthDiff / 12)}年前`;
    }
  };

  const calculateAge = (birthDate: Date, targetDate: Date) => {
    let age = targetDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = targetDate.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && targetDate.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const calculateGenerationInfo = () => {
    if (!review.first_release_date || !review.user_birth_date || !review.play_start_date) return null;
    
    const release = new Date(review.first_release_date);
    const birth = new Date(review.user_birth_date);
    const playStart = new Date(review.play_start_date);
    
    const ageAtRelease = calculateAge(birth, release);
    const ageAtPlay = calculateAge(birth, playStart);
    
    let generationType: string;

    if (ageAtRelease < 0) {
      generationType = 'GG';
    } else if (ageAtRelease <= 10) {
      generationType = 'Ch';
    } else if (ageAtRelease <= 18) {
      generationType = 'YT';
    } else {
      generationType = 'CT';
    }

    if (review.age_privacy === 0) {
      return null; // 非公開の場合は null を返す
    } else if (review.age_privacy === 1) {
      return generationType; // 曖昧な場合は世代タイプのみ返す
    } else {
      return `${generationType}${ageAtRelease >= 0 ? '+' : ''}${ageAtRelease}/${ageAtPlay}`;
    }
  };

  const generationInfo = calculateGenerationInfo();

  const getGenerationColor = (generationType: string) => {
    switch (generationType) {
      case 'GG': return theme.palette.error.light;
      case 'Ch': return theme.palette.success.light;
      case 'YT': return theme.palette.info.light;
      case 'CT': return theme.palette.warning.main;
      default: return theme.palette.grey[300];
    }
  };

  const getGenerationFullName = (generationType: string) => {
    switch (generationType) {
      case 'GG': return '後世生まれ / Generation Gap';
      case 'Ch': return '同時代体験（幼少期）/ Childhood';
      case 'YT': return '同時代体験（思春期）/ Youth';
      case 'CT': return '同時代体験 / Contemporary';
      default: return '不明';
    }
  };

  const handleGenerationClick = () => {
    setDialogOpen(true);
  };

  const calculateDaysDifference = (date1: Date, date2: Date) => {
    const diffTime = Math.abs(date2.getTime() - date1.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const formatDate = (date: Date) => `${date.getFullYear()}年${date.getMonth() + 1}月`;

  const getAgeDescription = (age: number, agePrivacy: number): string => {
    if (agePrivacy === 2) {
      return age.toString() + '歳';
    } else if (agePrivacy === 1) {
      if (age < 10) return '10歳未満';
      if (age < 15) return '10代前半';
      if (age < 20) return '10代後半';
      const decade = Math.floor(age / 10) * 10;
      return `${decade}代${age % 10 < 5 ? '前半' : '後半'}`;
    }
    return '非公開';
  };

  const getGGDescription = (monthsDiff: number, agePrivacy: number): string => {
    if (agePrivacy === 2) {
      if (monthsDiff < 12) {
        return `発売${monthsDiff}ヶ月後に生まれ`;
      }
      const years = Math.floor(monthsDiff / 12);
      return `発売${years}年後に生まれ`;
    } else if (agePrivacy === 1) {
      if (monthsDiff < 60) return '発売後5年未満で生まれ';
      if (monthsDiff < 120) return '発売後5～10年後に生まれ';
      return '発売から10年以上経過して生まれ';
    }
    return '発売後に生まれ';
  };

  const handleLikeClick = async () => {
    if (!localStorage.getItem('token')) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    try {
      if (userLikeCount >= 10) {
        console.error('いいねの上限（10回）に達しています。');
        return;
      }
      const response = await likeReview(review.id);
      setIsLiked(true);
      setLikeCount(response.data.count);
      setUserLikeCount(response.data.like_count);
    } catch (error: any) {
      console.error('イイね処理に失敗しました', error);
      if (error?.response?.data?.error) {
        console.error(error.response.data.error);
      }
    }
  };

  const handleHelpfulClick = async (helpful: boolean) => {
    try {
      if (isHelpful === helpful) {
        // 同じボタンを再度クリックした場合は評価を削除
        await removeReviewHelpfulness(review.id);
        setIsHelpful(null);
        setHelpfulCount(prev => helpful ? prev - 1 : prev);
        setUnhelpfulCount(prev => helpful ? prev : prev - 1);
      } else {
        // 新しい評価または評価の変更
        await toggleReviewHelpfulness(review.id, helpful);
        setIsHelpful(helpful);
        if (isHelpful !== null) {
          // 評価を変更する場合
          setHelpfulCount(prev => helpful ? prev + 1 : prev - 1);
          setUnhelpfulCount(prev => helpful ? prev - 1 : prev + 1);
        } else {
          // 新しく評価する場合
          setHelpfulCount(prev => helpful ? prev + 1 : prev);
          setUnhelpfulCount(prev => helpful ? prev : prev + 1);
        }
      }
    } catch (error) {
      console.error('レビューの評価に失敗しました:', error);
    }
  };

  const dialogContent = (
    <>
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'inline-block',
            bgcolor: getGenerationColor(generationInfo?.slice(0, 2) || ''),
            px: 1,
            py: 0.5,
            borderRadius: 1,
            mb: 0.5,
          }}
        >
          <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
            {generationInfo}
          </Typography>
        </Box>
        <Typography variant="subtitle1" >
          {getGenerationFullName(generationInfo?.slice(0, 2) || '')}
        </Typography>
      </Box>
      <Typography paragraph>
        {(() => {
          const releaseDate = review.first_release_date ? new Date(review.first_release_date) : null;
          const birthDate = review.user_birth_date ? new Date(review.user_birth_date) : null;
          const playDate = review.play_start_date ? new Date(review.play_start_date) : null;
          const agePrivacy = review.age_privacy;
          
          if (!releaseDate || !birthDate || !playDate) return '日付情報が不足しています。';

          const ageAtRelease = calculateAge(birthDate, releaseDate);
          const ageAtPlay = calculateAge(birthDate, playDate);
          
          if (birthDate > releaseDate) {
            const monthsDiff = (birthDate.getFullYear() - releaseDate.getFullYear()) * 12 + 
                               (birthDate.getMonth() - releaseDate.getMonth());
            const ggDescription = getGGDescription(monthsDiff, agePrivacy);
            return `このレビュアーは${ggDescription}、${formatDate(playDate)}（${getAgeDescription(ageAtPlay, agePrivacy)}のとき）にプレイしました。`;
          } else {
            return `このレビュアーはゲーム発売時（${formatDate(releaseDate)}）に${getAgeDescription(ageAtRelease, agePrivacy)}で、${formatDate(playDate)}（${getAgeDescription(ageAtPlay, agePrivacy)}のとき）にプレイしました。`;
          }
        })()}
        {review.created_at ? `${formatDate(new Date(review.created_at))}にレビューを投稿しました。` : ''}
      </Typography>
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        世代指標：
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        レビュワーの世代によって、ゲームが作られた時代の技術的制約や社会的背景への理解度が異なります。例えば、同時代を生きた人と後世に生まれた人では、ゲームの革新性や影響力の捉え方が大きく異なる可能性があります。
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 1 }}>
        また、ゲームの評価基準は世代によって異なる傾向があり、各世代特有の経験や知識が「良いゲーム」の捉え方に影響を与えることがあります。幼少期や青春期に体験したゲームへの感情的愛着が評価に影響を与えることもありますが、この指標によってその可能性を認識できます。
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 1 }}>
        世代指標やプロフィール（プレイ履歴,評価）に注目することで、レビューの文脈をより深く理解でき、個々のレビュワーの経験や視点が評価にどのように影響しているかを把握しやすくなります。ゲームへの不当な低評価や過度に好意的な評価を識別する手がかりにもなるでしょう。
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 1 }}>
      留意すべき点として、CT（同時代体験）のライトゲーマーと、CH,YT,GGのヘビーゲーマーや熱心な勉強家であれば、後者のほうがゲームの技術的制約や、ゲーム史、当時の社会的背景への理解度さえも習熟している可能性があります。
      </Typography>
    </>
  );

  const getScoreColor = (score: number | null) => {
    if (score === null) return "#666666";
    if (score >= 4.5) return "#4CAF50";
    if (score >= 4.0) return "#8BC34A";
    if (score >= 3.5) return "#CDDC39";
    if (score >= 3.0) return "#FFEB3B";
    if (score >= 2.5) return "#FFC107";
    if (score >= 2.1) return "#FF9800";
    return "#F44336";
  };

  const isOnline = () => {
    if (!review.last_access) return false;
    const lastAccess = new Date(review.last_access);
    const now = new Date();
    const diffInMinutes = (now.getTime() - lastAccess.getTime()) / (1000 * 60);
    return diffInMinutes <= 5;
  };

  const getStatusBadge = () => {
    if (!review.status) return null;
    
    const statusConfig = {
      2: { label: 'プレイ中', color: '#2196F3' },  // 青色
      3: { label: '中断', color: '#FF9800' }       // オレンジ色
    };

    const status = statusConfig[review.status as keyof typeof statusConfig];
    if (!status) return null;

    return (
      <Typography
        variant="caption"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          bgcolor: `${status.color}20`,
          color: 'rgba(255, 255, 255, 0.788)',
          px: 0.75,
          py: 0.25,
          borderRadius: 1,
          fontSize: '0.6rem',
          lineHeight: 1,
          border: `1px solid ${status.color}40`,
        }}
      >
        {status.label}
      </Typography>
    );
  };

  const renderAchievementIcon = () => {
    // サブステータスが配列として渡されているかどうかをチェック
    const subStatusArray = Array.isArray(review.sub_status) ? review.sub_status : 
                            (review.sub_status ? [review.sub_status] : []);

    // コンプリート（ステータス1でサブステータス7）の場合
    if (review.status === 1 && subStatusArray.includes(7)) {
      return (
        <Tooltip title="実績コンプリート" arrow>
          <Box
            sx={{
              background: 'linear-gradient(45deg, #2088ffc4, #a455ffa4)',
              borderRadius: '4px',
              padding: '1px 3px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 'fit-content',
              minHeight: '16px',
            }}
          >
            <EmojiEventsIcon sx={{ 
              color: '#eeeeeeff', 
              fontSize: '0.7rem'
            }} />
          </Box>
        </Tooltip>
      );
    }

    // 未クリア（サブステータス8）の場合
    if (subStatusArray.includes(8)) {
      return (
        <Tooltip title="未クリア" arrow>
          <Box
            sx={{
              background: 'rgba(100, 100, 100, 0.6)',
              borderRadius: '4px',
              padding: '1px 3px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 'fit-content',
              minHeight: '16px',
            }}
          >
            <RemoveCircleOutlineIcon sx={{ 
              color: 'white', 
              fontSize: '0.7rem'
            }} />
          </Box>
        </Tooltip>
      );
    }

    // リタイア（サブステータス9）の場合
    if (subStatusArray.includes(9)) {
      return (
        <Tooltip title="リタイア" arrow>
          <Box
            sx={{
              background: 'rgba(255, 78, 65, 0.4)',
              borderRadius: '4px',
              padding: '1px 3px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 'fit-content',
              minHeight: '16px',
            }}
          >
            <BlockIcon sx={{ 
              color: 'white', 
              fontSize: '0.7rem'
            }} />
          </Box>
        </Tooltip>
      );
    }

    return null;
  };

  return (
    <Box sx={{ 
      mb: 1, 
      p: { xs: 1.5, sm: 2 }, 
      bgcolor: 'rgba(255, 255, 255, 0.03)',
      borderRadius: 2,
      border: '1px solid rgba(255, 255, 255, 0.08)',
      transition: 'background-color 0.2s ease',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ position: 'relative', mr: 1.5 }}>
          <Avatar 
            src={review.avatar || undefined} 
            alt={review.username} 
            sx={{ 
              width: 40, 
              height: 40,
              border: '1px solid rgba(255, 255, 255, 0.08)'
            }}
          />
          {/* {isOnline() && (
            <Box
              sx={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                width: 12,
                height: 12,
                bgcolor: '#28a745',
                border: '2px solid',
                borderColor: 'background.paper',
                borderRadius: '50%',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': {
                    boxShadow: '0 0 0 0 rgba(40, 167, 69, 0.6)',
                  },
                  '70%': {
                    boxShadow: '0 0 0 6px rgba(40, 167, 69, 0)',
                  },
                  '100%': {
                    boxShadow: '0 0 0 0 rgba(40, 167, 69, 0)',
                  },
                },
              }}
            />
          )} */}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 0.5,
            flexWrap: 'wrap'
          }}>
            <MuiLink
              component={RouterLink}
              to={`/profile/${review.username}`}
              variant="subtitle1"
              sx={{ 
                textDecoration: 'none', 
                color: 'primary.main',
                lineHeight: 1,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {review.display_name || review.username}
            </MuiLink>
            {getStatusBadge()}
          </Box>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexWrap: 'wrap', 
            gap: 1,
            color: 'rgba(255, 255, 255, 0.5)'
          }}>
            {effectivePlayTimeHours(review.play_time, review.steam_playtime_min) ? (
              <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', color: 'pulse.info' }}>
                <TimerOutlinedIcon fontSize="inherit" sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                {effectivePlayTimeHours(review.play_time, review.steam_playtime_min)}時間
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', color: 'pulse.info' }}>
                <TimerOutlinedIcon fontSize="inherit" sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                -
              </Typography>
            )}
            {review.play_start_date ? (
              <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', color: 'pulse.info' }}>
                <PlayCircleOutlineIcon fontSize="inherit" sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                {formatPlayDate(review.play_start_date)}
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', color: 'pulse.info' }}>
                <PlayCircleOutlineIcon fontSize="inherit" sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                -
              </Typography>
            )}
            {review.platform && review.platform.length > 0 ? (
              <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', color: 'pulse.info' }}>
                <SportsEsportsOutlinedIcon fontSize="inherit" sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                {getPlatformDisplayText(review.platform)}
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', color: 'pulse.info' }}>
                <SportsEsportsOutlinedIcon fontSize="inherit" sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                -
              </Typography>
            )}
            {generationInfo && (
              <Typography
                variant="caption"
                onClick={handleGenerationClick}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0.8)',
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 1,
                  lineHeight: 1,
                  color: 'text.secondary',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                {generationInfo}
              </Typography>
            )}
            {renderAchievementIcon()}
          </Box>
        </Box>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexWrap: 'wrap', 
        mb: { xs: 1, sm: 1 },
        gap: { xs: 0.5, sm: 1 },
        pl: { xs: 0, sm: 6.5 } // アバターの幅 + marginRight に合わせる
      }}>
        {/* 総合スコアとスコアタグ */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          gap: 1
        }}>
          {review.score !== null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                  py: 0.25,
                  px: 0.75,
                  lineHeight: "1",
                  borderRadius: 1,
                  backgroundColor: `${getScoreColor(review.score)}B3`,
                }}
              >
                {review.score.toFixed(1)}
              </Typography>
              <Box
                sx={{
                  width: 80,
                  height: 4,
                  bgcolor: "rgba(255, 255, 255, 0.3)",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${((review.score - 1) / 4) * 100}%`,
                    height: "100%",
                    bgcolor: getScoreColor(review.score),
                    borderRadius: 2,
                    transition: "width 0.3s ease",
                  }}
                />
              </Box>
            </Box>
          )}
          {review.score_tags && review.score_tags.map((tagId) => {
            const tag = scoreTags.find(t => t.id === tagId);
            return tag && (
              <Chip
                key={tag.id}
                icon={
                  tag.id === 9 || tag.id >= 30 ? (
                    <KeyboardDoubleArrowDownIcon style={{ color: '#79bcf3' }} />
                  ) : (
                    <KeyboardDoubleArrowUpIcon style={{ color: '#f37979' }} />
                  )
                }
                label={tag.label}
                size="small"
                variant="outlined"
                sx={{ 
                  mr: 0.5, 
                  mb: 0.5, 
                  height: '20px',
                  '& .MuiChip-icon': { fontSize: '0.7rem' },
                  '& .MuiChip-label': { 
                    fontSize: '0.6rem',
                    py: 0.25,
                  }
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* 詳細スコア（全幅） */}
      <Box sx={{ 
        mb: { xs: 1, sm: 1.5 },
        pl: { xs: 0, sm: 6.5 } // PCでは他のコンテンツと左余白を揃える
      }}>
        <DetailScores
          scoreVisual={review.score_visual}
          scoreSound={review.score_sound}
          scoreGameplay={review.score_gameplay}
          scoreNarrative={review.score_narrative}
          scoreOriginality={review.score_originality}
        />
      </Box>

      <Box sx={{ 
        mb: 1.5,
        position: 'relative',
        pl: { xs: 0, sm: 6.5 },
        color: 'pulse.text'
      }}>
        <Typography variant="body2" component="div" lineHeight="1.3" sx={{
          fontSize: '0.725rem',
          '& .spoiler': {
            backgroundColor: '#444',
            padding: '0 4px',
            borderRadius: '2px',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }
        }}>
          {formatReviewText(truncatedText, isMobile)}
        </Typography>
        {(review.review_text.length > maxLength || review.review_text.split('\n').length > maxLines) && !expanded && (
          <MuiLink
            component="span"
            onClick={toggleExpand}
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              cursor: 'pointer',
              color: 'primary.main',
              fontSize: '0.7rem',
              fontWeight: 'normal',
              lineHeight: '1.5',
              backgroundColor: 'background.paper',
              pl: 1,
            }}
          >
            全て読む
          </MuiLink>
        )}
      </Box>

      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pl: { xs: 0, sm: 6.5 },
        mt: 1.5
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1 
        }}>
          {review.review_text && (
            <Button
              onClick={handleLikeClick}
              startIcon={isLiked ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
              color={isLiked ? "primary" : "inherit"}
              disabled={userLikeCount >= 10}
              size="small"
              sx={{ 
                minWidth: 'auto',
                px: 1.5,
                py: 0.25,
                fontSize: '0.75rem',
                borderRadius: 1,
                lineHeight: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                },
                '& .MuiButton-startIcon': {
                  mr: 1,
                  '& svg': {
                    fontSize: '0.8rem'
                  }
                },
                '&.Mui-disabled': {
                  opacity: 0.7,
                  color: theme.palette.primary.main
                }
              }}
            >
              <Typography variant="caption" color="text.primary" lineHeight={1}>
                {likeCount}
                {userLikeCount > 0 && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 'bold'
                    }}
                  >
                    {` (+${userLikeCount})`}
                  </Typography>
                )}
              </Typography>
            </Button>
          )}
          <Typography variant="caption" color="pulse.info" lineHeight={1}>
            {new Date(review.created_at).toLocaleDateString()}に投稿
            {review.updated_at && 
             new Date(review.updated_at).toLocaleDateString() !== new Date(review.created_at).toLocaleDateString() && (
              <>
                <UpdateIcon sx={{ fontSize: '0.8rem', ml: 1, mr: 0.25, verticalAlign: 'text-bottom' }} />
                {new Date(review.updated_at).toLocaleDateString()}
              </>
            )}
          </Typography>
        </Box>
        {review.review_text && (
          <MuiLink
            component={RouterLink}
            to={`/game/${review.game}/reviews/${review.id}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              textDecoration: 'none',
              fontSize: '0.75rem',
              '&:hover': {
                color: 'primary.main'
              }
            }}
          >
            <OpenInNewIcon sx={{ fontSize: '1rem', ml: 0.5 }} />
          </MuiLink>
        )}
      </Box>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="世代指標"
        content={dialogContent}
      />
      {review.review_text && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 1,
          gap: 2,
          flexWrap: 'wrap',
          pl: { xs: 0, sm: 6.5 }
        }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Button
              onClick={() => handleHelpfulClick(true)}
              size="small"
              startIcon={<KeyboardArrowUpIcon fontSize="small" sx={{ color: isHelpful === true ? '#d9ff00' : 'text.secondary' }}/>}
              variant="outlined"
              sx={{
                color: isHelpful === true ? 'white' : 'text.secondary',
                borderColor: isHelpful === true ? 'divider' : 'divider',
                backgroundColor: isHelpful === true ? '#d9ff0017' : 'transparent',
                fontSize: '0.75rem',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: isHelpful === true ? '#8b8d801c' : 'rgba(255, 255, 255, 0.08)',
                  borderColor: isHelpful === true ? 'transparent' : 'primary.main',
                },
                '& .MuiButton-startIcon': {
                  mr: 0.5,
                  '& svg': {
                    fontSize: '0.8rem'
                  }
                }
              }}
            >
              <Typography variant="caption" color={isHelpful === true ? "white" : "text.secondary"} lineHeight={1}>
                参考になった
              </Typography>
            </Button>
            <Button
              onClick={() => handleHelpfulClick(false)}
              size="small"
              variant="outlined"
              startIcon={<KeyboardArrowDownIcon fontSize="small" />}
              sx={{
                color: isHelpful === false ? 'white' : 'text.secondary',
                borderColor: isHelpful === false ? 'divider' : 'divider',
                backgroundColor: isHelpful === false ? 'rgba(211, 47, 47, 0.12)' : 'transparent',
                minWidth: 'auto',
                fontSize: '0.75rem',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: isHelpful === false ? 'rgba(211, 47, 47, 0.2)' : 'rgba(255, 255, 255, 0.08)',
                  borderColor: isHelpful === false ? 'transparent' : 'error.main',
                },
                '& .MuiButton-startIcon': {
                  mr: 0,
                  '& svg': {
                    fontSize: '0.8rem'
                  }
                }
              }}
            >
            </Button>
            {/* {(helpfulCount > 0 || unhelpfulCount > 0) && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {helpfulCount}人が参考になったと投票
              </Typography>
            )} */}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ReviewItem;